/* Greenleaf — the motion system's two moving parts. 12 Oct 2026, Andrew's yes (via the Coordinator).
 *
 * 1. SECTIONS FADE UP ONCE. Each page section that starts BELOW the first screen is lowered 10px and made
 *    transparent, then settles into place the first time it scrolls into view, and is never touched again.
 *    Nothing on the first screen starts hidden, so the page never opens blank. Skipped entirely under reduced
 *    motion, without IntersectionObserver, and in the theme editor. The header and footer groups never fade.
 *    A section that draws something pinned to the screen (the filter sheet, the phone buy bar, the bag) is
 *    skipped too: while a section is lowered it becomes the frame a pinned child is placed against.
 *    The look (distance, time, easing) is in the MOTION SYSTEM block of preview/shared/site.css.
 *
 * 2. "IT'S 3:42PM IN WAILEA". Fills every [data-gl-now] with the time in Hawai‘i (Pacific/Honolulu, which
 *    has no daylight saving) and refreshes it on the minute. The slot is hidden in the markup and only shown
 *    once it has a time, so without this file nothing appears.
 *
 * 3. "SUNSET TONIGHT 6:03PM" (13 Oct 2026, Andrew's yes via the Coordinator) follows the time on the same line.
 *    Worked out here with NOAA's solar formula for Wailea (20.69 N, 156.44 W; the sun 0.833 degrees below the
 *    horizon, which is how published tables define sunset). After today's sunset it gives tomorrow's.
 *    Checked against the US Naval Observatory's published Wailea times on five dates (13 Oct, 21 Dec 2026;
 *    10 Jan, 20 Mar, 21 Jun 2027): every one within a minute. Hawai‘i keeps one clock all year (UTC-10).
 *
 * 4. THE ABOUT TIMELINE FILLS AS YOU SCROLL (13 Oct 2026, Andrew: "do it well though"). On .tl[data-gl-tl] the
 *    line from 1992 to Later grows with the scroll (left to right on a computer, top to bottom on a phone) and
 *    each year lights up as the line reaches its dot. It follows the scroll both ways. Only the variable --tl-p
 *    (0 to 1) and an is-lit class per year change; the line itself is a transform, so nothing re-lays-out. The
 *    row starts filling when it is near the bottom of the screen and is full by the time it is two-fifths of
 *    the way up; on a phone a reading line 60% down the screen draws it. Without this file, or under reduced
 *    motion, the timeline is shown whole, as before.
 *
 * 5. STORE PHOTOS DRIFT (13 Oct 2026). [data-gl-drift] frames (the big unframed photos on About and Visit) move
 *    their photo up to 3% of its height against the scroll, scaled 7% so no edge ever shows. Transform only,
 *    computed once per frame while a photo is on screen. Computers only (901px and wider): phones keep still
 *    photos. Off under reduced motion.
 *
 * 6. BIG PHOTOS ARRIVE ONCE (13 Oct 2026). The big editorial photos (.ph, .withphoto__ph, .storecard__media,
 *    .shot) that start below the first screen wait hidden and come in once, when on screen AND loaded:
 *    "settle" (fade in from 4% larger) or "focus" (fade in from a blur), chosen by html[data-gl-photo-in].
 *    A photo that fails to load is shown anyway after 3s, so nothing can stay hidden.
 *
 * 7. THE PRODUCT PHOTO BREATHES (13 Oct 2026). After two quiet seconds on a product page the main photo eases
 *    between 100% and 102% over 8s each way. Any pointer press, key, wheel, touch, scroll, or the pointer
 *    entering the photo stops it at once and settles it back over 300ms; ten quiet seconds start it again.
 */
(function () {
  'use strict';
  if (window.__glMotion) return;
  window.__glMotion = true;

  /* ---------- 1. fade up once ---------- */
  function reveal() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window) || window.Shopify && window.Shopify.designMode) return;
    var SKIP = '.fr, [data-sticky-bar], .bagdrawer, dialog, .header-fixed';
    var sections = document.querySelectorAll('main > .shopify-section');
    var fold = window.innerHeight || document.documentElement.clientHeight;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        io.unobserve(el);
        el.classList.add('gl-rv--in');
        var done = function () { el.classList.remove('gl-rv', 'gl-rv--in'); };
        el.addEventListener('transitionend', function te(ev) {
          if (ev.target !== el) return;
          el.removeEventListener('transitionend', te);
          done();
        });
        setTimeout(done, 900); /* if transitionend never fires (tab in the background), still clean up */
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    /* SAFETY NET: a jump (the End key, a #link, back-to-top) can carry a section past the screen without it ever
       intersecting, and it would stay invisible above. Any lowered section whose bottom is above the screen is
       shown at once, without the animation. */
    var waiting = [];
    var sweep = function () {
      for (var j = waiting.length - 1; j >= 0; j--) {
        var el = waiting[j];
        if (!el.classList.contains('gl-rv') || el.classList.contains('gl-rv--in')) { waiting.splice(j, 1); continue; }
        if (el.getBoundingClientRect().bottom < 0) { io.unobserve(el); el.classList.remove('gl-rv'); waiting.splice(j, 1); }
      }
      if (!waiting.length) window.removeEventListener('scroll', onScroll);
    };
    var ticking = false;
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; sweep(); });
    };
    for (var i = 0; i < sections.length; i++) {
      var s = sections[i];
      if (/shopify-section-group-/.test(s.className)) continue;
      if (s.getBoundingClientRect().top < fold) continue;
      if (s.querySelector(SKIP)) continue;
      if (!s.offsetHeight) continue;
      s.classList.add('gl-rv');
      io.observe(s);
      waiting.push(s);
    }
    if (waiting.length) window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- 2. the time in Wailea ---------- */
  /* NOAA solar calculator: minutes after UTC midnight of the sunset on calendar day y-m-d at lat/lon (west is
     negative). Two passes: the first at local noon, the second at the sunset it found, which is good to seconds. */
  function sunsetUTC(y, m, d, lat, lon) {
    var rad = Math.PI / 180;
    function at(utcMin) {
      var jd = Date.UTC(y, m - 1, d) / 864e5 + 2440587.5 + utcMin / 1440;
      var t = (jd - 2451545) / 36525;
      var L0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
      var M = 357.52911 + t * (35999.05029 - 0.0001537 * t);
      var e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
      var C = Math.sin(M * rad) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
              Math.sin(2 * M * rad) * (0.019993 - 0.000101 * t) + Math.sin(3 * M * rad) * 0.000289;
      var omega = 125.04 - 1934.136 * t;
      var lambda = L0 + C - 0.00569 - 0.00478 * Math.sin(omega * rad);
      var eps = 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60 + 0.00256 * Math.cos(omega * rad);
      var decl = Math.asin(Math.sin(eps * rad) * Math.sin(lambda * rad));
      var yv = Math.pow(Math.tan(eps * rad / 2), 2);
      var eqt = 4 / rad * (yv * Math.sin(2 * L0 * rad) - 2 * e * Math.sin(M * rad) +
                4 * e * yv * Math.sin(M * rad) * Math.cos(2 * L0 * rad) - 0.5 * yv * yv * Math.sin(4 * L0 * rad) -
                1.25 * e * e * Math.sin(2 * M * rad));
      var cosH = Math.cos(90.833 * rad) / (Math.cos(lat * rad) * Math.cos(decl)) - Math.tan(lat * rad) * Math.tan(decl);
      var H = Math.acos(Math.max(-1, Math.min(1, cosH))) / rad;
      return 720 - 4 * (lon - H) - eqt;
    }
    return at(at(720 - 4 * lon));
  }

  /* "Sunset tonight 6:03pm", or "Sunset tomorrow 6:02pm" once tonight's has passed. Non-breaking spaces so the
     phrase never splits across two lines. */
  function sunsetText() {
    var HST = -600;                                    /* Hawai'i: UTC-10, no daylight saving */
    var now = new Date(Date.now() + HST * 60000);      /* read with getUTC* = the date and time in Wailea */
    var y = now.getUTCFullYear(), mo = now.getUTCMonth() + 1, d = now.getUTCDate();
    var nowMin = now.getUTCHours() * 60 + now.getUTCMinutes();
    var when = 'tonight';
    var set = Math.round(sunsetUTC(y, mo, d, 20.69, -156.44) + HST);
    if (nowMin >= set) {
      var t = new Date(Date.UTC(y, mo - 1, d + 1));
      set = Math.round(sunsetUTC(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate(), 20.69, -156.44) + HST);
      when = 'tomorrow';
    }
    set = (set + 1440) % 1440;
    var h = Math.floor(set / 60), mi = set % 60;
    var label = (h % 12 || 12) + ':' + (mi < 10 ? '0' : '') + mi + (h < 12 ? 'am' : 'pm');
    return 'Sunset\u00a0' + when + '\u00a0' + label;
  }

  function clock() {
    var slots = document.querySelectorAll('[data-gl-now]');
    if (!slots.length || !window.Intl || !Intl.DateTimeFormat) return;
    var fmt;
    try {
      fmt = new Intl.DateTimeFormat('en-US', { timeZone: 'Pacific/Honolulu', hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (e) { return; }
    function tick() {
      var h = '', m = '', p = '';
      fmt.formatToParts(new Date()).forEach(function (x) {
        if (x.type === 'hour') h = x.value;
        else if (x.type === 'minute') m = x.value;
        else if (x.type === 'dayPeriod') p = x.value.toLowerCase().replace(/\./g, '');
      });
      var text = 'It’s ' + h + ':' + m + p + ' in Wailea';
      try { text += '\u00a0· ' + sunsetText(); } catch (e) { /* the time alone is still right */ }
      for (var i = 0; i < slots.length; i++) {
        if (slots[i].textContent !== text) slots[i].textContent = text;
        slots[i].hidden = false;
      }
      setTimeout(tick, 60000 - Date.now() % 60000 + 50);
    }
    tick();
  }

  /* ---------- 4 + 5. scroll-linked: the timeline and the drifting photos ---------- */
  function scrolly() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) return;
    var tls = [].slice.call(document.querySelectorAll('.tl[data-gl-tl]'));
    var drifts = [].slice.call(document.querySelectorAll('[data-gl-drift]'));
    if (!tls.length && !drifts.length) return;
    var wide = window.matchMedia('(min-width: 901px)');
    var visible = new Set();
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target); });
      queue();
    }, { rootMargin: '10% 0px 10% 0px' });

    tls.forEach(function (tl) {
      tl.classList.add('is-live');
      tl._stops = [].slice.call(tl.children);
      io.observe(tl);
    });
    drifts.forEach(function (f) {
      f._img = f.querySelector('img');
      if (f._img) io.observe(f);
    });

    function vertical(tl) { return getComputedStyle(tl, '::before').width === '1px'; }
    function drawTl(tl, vh) {
      var r = tl.getBoundingClientRect(), vert = vertical(tl), p;
      if (vert) p = (vh * 0.6 - r.top) / r.height;
      else p = (vh * 0.88 - r.top) / (vh * 0.48);
      p = Math.max(0, Math.min(1, p));
      if (tl._p === p) return;
      tl._p = p;
      tl.style.setProperty('--tl-p', p.toFixed(4));
      var reach = p * (vert ? r.height : r.width) + 2;
      tl._stops.forEach(function (li) {
        var at = vert ? li.offsetTop + 12 : li.offsetLeft + 6;
        li.classList.toggle('is-lit', reach >= at || p >= 1);
      });
    }
    function drawDrift(f, vh) {
      if (!wide.matches) { if (f._on) { f._img.style.transform = ''; f.classList.remove('is-drifting'); f._on = false; } return; }
      var r = f.getBoundingClientRect();
      var k = ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2);   /* -1 above .. 1 below */
      k = Math.max(-1, Math.min(1, k));
      if (!f._on) { f.classList.add('is-drifting'); f._on = true; }
      f._img.style.transform = 'translate3d(0,' + (k * 3).toFixed(3) + '%,0) scale(1.07)';
    }
    var ticking = false;
    function frame() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      visible.forEach(function (el) { if (el._stops) drawTl(el, vh); else drawDrift(el, vh); });
    }
    function queue() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', function () { tls.forEach(function (t) { t._p = -1; }); queue(); }, { passive: true });
    queue();
  }

  /* ---------- 6. big photos arrive once ---------- */
  function photosIn() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window) || window.Shopify && window.Shopify.designMode) return;
    var fold = window.innerHeight || document.documentElement.clientHeight;
    var imgs = document.querySelectorAll('main .ph > img, main .withphoto__ph > img, main .storecard__media > img, main .shot > img');
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        var img = e.target; io.unobserve(img);
        var go = function () {
          if (img._done) return; img._done = true;
          requestAnimationFrame(function () { img.classList.add('gl-pi--in'); });
          setTimeout(function () { img.classList.remove('gl-pi', 'gl-pi--in'); }, 1100);
        };
        if (img.complete && img.naturalWidth) go();
        else { img.addEventListener('load', go, { once: true }); img.addEventListener('error', go, { once: true }); setTimeout(go, 3000); }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    for (var i = 0; i < imgs.length; i++) {
      var im = imgs[i], r = im.getBoundingClientRect();
      if (r.top < fold || r.width < 320) continue;
      im.classList.add('gl-pi');
      io.observe(im);
    }
  }

  /* ---------- 7. the product photo breathes ---------- */
  function breathe() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var stage = document.querySelector('.stage[data-stage]');
    if (reduce || !stage) return;
    var timer = null, on = false;
    function img() { return stage.querySelector('.stage__view--photo[data-active]:not(.is-zoomed) > img'); }
    function begin() { if (document.hidden) { later(); return; } stage.classList.add('gl-breathe'); on = true; }
    function later() { clearTimeout(timer); timer = setTimeout(begin, 10000); }
    function stop() {
      if (on) {
        var el = img(), sc = el ? getComputedStyle(el).scale : 'none';
        stage.classList.remove('gl-breathe'); on = false;
        if (el && sc && sc !== 'none' && sc !== '1') {
          el.style.scale = sc; void el.offsetWidth;
          el.style.transition = 'scale 300ms cubic-bezier(.22,.61,.36,1), transform 220ms cubic-bezier(.2,0,.2,1)';
          el.style.scale = '1';
          setTimeout(function () { el.style.scale = ''; el.style.transition = ''; }, 340);
        }
      }
      later();
    }
    ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach(function (t) {
      window.addEventListener(t, stop, { passive: true, capture: true });
    });
    stage.addEventListener('pointerenter', stop);
    clearTimeout(timer); timer = setTimeout(begin, 2000);
  }

  function start() { reveal(); clock(); scrolly(); photosIn(); breathe(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
